import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty } from '@src/core/Difficulty';
import { GameEvent } from '@src/core/EventQueue';
import { kill, NO_ENTITY, playClip, spawnDragon, spawnFx, spawnProjectile, type ProjectileSpec } from '@src/ecs/archetypes';
import { Anim, ClipId, DragonTag, Dying, FxTag, Lifetime, Player, Position, Sprite, SpriteId, Team, Velocity } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { BossState, emitParticles, ParticleKind, RoomPhase, type GameContext } from '@src/ecs/world';
import { Anims } from '@src/game/AnimDefs';
import { GATES, HALF_H, HALF_W, isInCorner } from '@src/game/arena/RoomLayout';
import { floorMod } from '@src/game/FloorModifiers';
import { WeaponId } from '@src/game/weapons/WeaponDefs';

const IDLE_HECKLE_AFTER = 10;
const IDLE_HECKLE_PERIOD = 18;
const MAX_IDLE_HECKLES_PER_ROOM = 1;
const DEG = Math.PI / 180;
/** Distance from the body center to the jaws along the heading, as a fraction of the sprite scale. */
const MOUTH_OFFSET = Config.dragon.scale * 0.46;
/** Flame jet sprite scale and half its rendered length, so the root sits on the mouth. */
const JET_SCALE = 2.6;
const JET_HALF_LENGTH = 3.05;

/** DragonTag.phase values. */
const PHASE_HUNT = 0;
const PHASE_WINDUP = 1;
const PHASE_BREATHE = 2;
const PHASE_LEAVE = 3;
const FLY_IN_SECONDS = 1.1;

/**
 * Stall penalty. Taking too long or camping a corner triggers a warning, then
 * Cinderwyrm: Vaelen's pet dragon flies in over the wall, hunts the player with
 * ramping speed and breathes fire cones. Unkillable; leaves when the room clears.
 */
export class DragonSystem implements GameSystem {
  private ctx!: GameContext;
  private warnedAt = 0;
  private lastIdleHeckle = -Infinity;
  private idleHeckles = 0;
  private lastRoom = -1;
  private flyIn = 0;
  private readonly flame: ProjectileSpec = {
    owner: -1,
    weapon: WeaponId.Wand,
    sprite: SpriteId.BoltFire,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    radius: 0.42,
    life: Config.dragon.flameLife,
    damage: Config.dragon.flameDamage,
    spawnerMult: 0,
    knockback: 4,
    pierce: 0,
    tint: 0xff9a3a,
    scale: 1.1,
    grow: 0,
    team: Team.Enemy,
  };

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const room = ctx.room;
    if (room.roomIndex !== this.lastRoom) {
      this.lastRoom = room.roomIndex;
      this.idleHeckles = 0;
    }
    const players = query(ctx.world, [Player, Position]);
    this.fly(players, dt);
    if (room.phase !== RoomPhase.Fight) return;
    // The floor boss owns the exit room once it is on stage; no stall punisher on top of it.
    if (room.bossState !== BossState.None) return;

    let cornerCamping = false;
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      if (Player.dead[eid]) continue;
      const inCorner = isInCorner(Position.x[eid], Position.y[eid]);
      Player.cornerTime[eid] = inCorner ? Player.cornerTime[eid] + dt : 0;
      if (Player.cornerTime[eid] > Config.dragon.cornerSeconds) cornerCamping = true;

      // Idle heckles: at most two per room, and none once the wyrm is announced.
      if (
        !room.dragonWarned &&
        this.idleHeckles < MAX_IDLE_HECKLES_PER_ROOM &&
        Player.idleTime[eid] > IDLE_HECKLE_AFTER &&
        ctx.time - this.lastIdleHeckle > IDLE_HECKLE_PERIOD
      ) {
        this.lastIdleHeckle = ctx.time;
        this.idleHeckles++;
        ctx.events.push(GameEvent.PlayerIdle, eid, 0, Position.x[eid], Position.y[eid]);
      }
    }

    const stallAfter = Config.dragon.stallSeconds * difficulty().stallSeconds * floorMod(ctx.floor.modifier).stallScale;
    const stalled = room.fightTime > stallAfter || cornerCamping;
    if (stalled && !room.dragonWarned) {
      room.dragonWarned = true;
      this.warnedAt = room.fightTime;
      ctx.events.push(GameEvent.StallWarning, cornerCamping ? 1 : 0, 0, 0, 0);
    }

    if (room.dragonWarned && room.dragonEid === -1 && room.fightTime - this.warnedAt >= Config.dragon.warningLead) {
      this.summon(players);
    }
  }

  private summon(players: ArrayLike<number>): void {
    const ctx = this.ctx;
    // Come in over the wall farthest from the (first living) player.
    let px = 0;
    let py = 0;
    for (let i = 0; i < players.length; i++) {
      if (Player.dead[players[i]]) continue;
      px = Position.x[players[i]];
      py = Position.y[players[i]];
      break;
    }
    let best = GATES[0];
    let bestD = -1;
    for (let g = 0; g < GATES.length; g++) {
      const d = Math.hypot(GATES[g].x - px, GATES[g].y - py);
      if (d <= bestD) continue;
      bestD = d;
      best = GATES[g];
    }
    // Start well outside the arena so the fly-in reads as coming over the wall.
    const eid = spawnDragon(ctx, best.x + best.nx * 6, best.y + best.ny * 6);
    // Nose pointed into the arena; the art faces +x.
    Sprite.rot[eid] = Math.atan2(-best.ny, -best.nx);
    this.flyIn = 0;
    ctx.room.dragonEid = eid;
    ctx.events.push(GameEvent.DragonSpawn, eid, 0, best.x, best.y);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 120);
    ctx.fx.shakeX += 0.8;
    ctx.fx.shakeY += 0.8;
    ctx.fx.rumble = 1;
  }

  private fly(players: ArrayLike<number>, dt: number): void {
    const ctx = this.ctx;
    const c = Config.dragon;
    const dragons = query(ctx.world, [DragonTag, Position, Velocity, Sprite]);
    for (let i = 0; i < dragons.length; i++) {
      const eid = dragons[i];
      if (Dying.flag[eid]) continue;
      const t = ctx.time;
      const phase = DragonTag.phase[eid];
      DragonTag.phaseTime[eid] += dt;

      // Altitude rides the wing beat: the body lifts through the downstroke.
      // Anim.time runs 0..duration through the fly loop (frame 1 = wings level).
      const flying = Anim.clip[eid] === ClipId.Idle || Anim.clip[eid] === ClipId.Run;
      const flapPhase = flying ? Anim.time[eid] / Math.max(0.05, Anims.duration(SpriteId.Dragon, Anim.clip[eid])) : 0.25;
      const beat = Math.sin(flapPhase * Math.PI * 2);
      DragonTag.bob[eid] = 0.3 + beat * 0.16;
      if (flying) Sprite.squash[eid] = beat * 0.08;

      if (phase === PHASE_LEAVE) {
        // Climb out over the nearest wall and fade.
        const pt = DragonTag.phaseTime[eid];
        const x = Position.x[eid];
        const y = Position.y[eid];
        const dirX = Math.abs(x) / HALF_W > Math.abs(y) / HALF_H ? Math.sign(x) || 1 : 0;
        const dirY = dirX === 0 ? Math.sign(y) || 1 : 0;
        Velocity.x[eid] = dirX * 14;
        Velocity.y[eid] = dirY * 14;
        this.steer(eid, Math.atan2(dirY, dirX), dt, 6);
        Sprite.alpha[eid] = Math.max(0, 1 - pt / 0.9);
        Sprite.scale[eid] = c.scale * (1 + pt * 0.3);
        this.dropJet(eid);
        if (pt > 1) kill(eid);
        continue;
      }

      // Fly-in: fade up and accelerate into the arena.
      this.flyIn = Math.min(1, this.flyIn + dt / FLY_IN_SECONDS);
      Sprite.alpha[eid] = Math.min(1, this.flyIn * 1.5);

      let tx = 0;
      let ty = 0;
      let best = Infinity;
      for (let p = 0; p < players.length; p++) {
        const pid = players[p];
        if (Player.dead[pid]) continue;
        const dx = Position.x[pid] - Position.x[eid];
        const dy = Position.y[pid] - Position.y[eid];
        const d2 = dx * dx + dy * dy;
        if (d2 >= best) continue;
        best = d2;
        tx = dx;
        ty = dy;
      }
      if (best === Infinity) {
        Velocity.x[eid] *= 0.95;
        Velocity.y[eid] *= 0.95;
        continue;
      }
      const d = Math.sqrt(best) || 1;
      const nx = tx / d;
      const ny = ty / d;

      if (phase === PHASE_HUNT) {
        DragonTag.speed[eid] = Math.min(c.maxSpeed, DragonTag.speed[eid] + c.accel * dt);
        const speed = DragonTag.speed[eid] * this.flyIn;
        // Swoop: weave across the chase line so the path is not a straight beeline.
        const weave = Math.sin(t * 2.2) * 0.35;
        Velocity.x[eid] = (nx - ny * weave) * speed;
        Velocity.y[eid] = (ny + nx * weave) * speed;
        this.steer(eid, Math.atan2(Velocity.y[eid], Velocity.x[eid]), dt, c.turnRate);
        if (ctx.rng.chance(dt * 6)) emitParticles(ctx.fx, ParticleKind.Embers, Position.x[eid], Position.y[eid], -nx, -ny, 0.5);

        DragonTag.breathTimer[eid] -= dt;
        if (DragonTag.breathTimer[eid] <= 0 && this.flyIn >= 1 && d < 10) {
          DragonTag.phase[eid] = PHASE_WINDUP;
          DragonTag.phaseTime[eid] = 0;
          playClip(eid, ClipId.Cast, true, true);
          // Fit the roar clip so the jaws are wide as the windup ends.
          Anim.speed[eid] = Math.max(0.5, Anims.duration(SpriteId.Dragon, ClipId.Cast) / c.breathWindup);
          ctx.events.push(GameEvent.DragonRoar, eid, 0, Position.x[eid], Position.y[eid]);
          ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 0.5;
          ctx.fx.shakeY += 0.4;
        }
      } else if (phase === PHASE_WINDUP) {
        // Hover and telegraph: rear back, swing the head onto the player, then unleash.
        Velocity.x[eid] *= 0.85;
        Velocity.y[eid] *= 0.85;
        this.steer(eid, Math.atan2(ny, nx), dt, c.turnRate * 1.1);
        Sprite.squash[eid] = -0.3;
        const glow = DragonTag.phaseTime[eid] / c.breathWindup;
        if (ctx.rng.chance(dt * 18 * glow)) {
          const mx = Position.x[eid] + Math.cos(Sprite.rot[eid]) * 1.6;
          const my = Position.y[eid] + Math.sin(Sprite.rot[eid]) * 1.6;
          emitParticles(ctx.fx, ParticleKind.Embers, mx, my, Math.cos(Sprite.rot[eid]), Math.sin(Sprite.rot[eid]), 0.6, 0xffb040);
        }
        if (DragonTag.phaseTime[eid] >= c.breathWindup) {
          DragonTag.phase[eid] = PHASE_BREATHE;
          DragonTag.phaseTime[eid] = 0;
          DragonTag.breathed[eid] = 0;
          // Hold the jaws-wide pose (last roar frame) for the whole stream.
          playClip(eid, ClipId.Cast, true, true);
          const roar = Anims.clip(SpriteId.Dragon, ClipId.Cast);
          Anim.frame[eid] = roar.len - 1;
          Anim.time[eid] = (roar.len - 1) / roar.fps + 0.001;
          Anim.speed[eid] = 0;
          this.igniteJet(eid);
          ctx.fx.shakeX += Math.cos(Sprite.rot[eid]) * 0.6;
          ctx.fx.shakeY += Math.sin(Sprite.rot[eid]) * 0.6;
          ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.8);
        }
      } else if (phase === PHASE_BREATHE) {
        // Recoil back along the jet and keep the nose on target while streaming.
        const heading = Sprite.rot[eid];
        const hx = Math.cos(heading);
        const hy = Math.sin(heading);
        Velocity.x[eid] -= hx * 5 * dt;
        Velocity.y[eid] -= hy * 5 * dt;
        this.steer(eid, Math.atan2(ny, nx), dt, c.turnRate * 0.6);
        Sprite.squash[eid] = 0.2;
        this.streamFlames(eid, hx, hy, dt);
        if (DragonTag.phaseTime[eid] >= c.breathDuration) {
          this.dropJet(eid);
          DragonTag.phase[eid] = PHASE_HUNT;
          DragonTag.breathTimer[eid] = c.breathInterval;
          Anim.speed[eid] = 1;
          playClip(eid, ClipId.Idle, true);
        }
      }
    }
  }

  /** Turn Sprite.rot toward `target` at a bounded rate (no snapping). */
  private steer(eid: number, target: number, dt: number, rate: number): void {
    const cur = Sprite.rot[eid];
    let diff = target - cur;
    diff = Math.atan2(Math.sin(diff), Math.cos(diff));
    const step = rate * dt;
    Sprite.rot[eid] = cur + Math.max(-step, Math.min(step, diff));
  }

  /** The visible fire: a looping jet sprite pinned to the mouth for the whole breath. */
  private igniteJet(eid: number): void {
    const ctx = this.ctx;
    this.dropJet(eid);
    const heading = Sprite.rot[eid];
    const jet = spawnFx(ctx, SpriteId.FlameJet, Position.x[eid], Position.y[eid], heading, JET_SCALE, 0xffffff, 0.95);
    if (jet === NO_ENTITY) return;
    Lifetime.t[jet] = Config.dragon.breathDuration + 0.12;
    DragonTag.jet[eid] = jet;
    this.placeJet(eid, jet);
    const mx = Position.x[eid] + Math.cos(heading) * MOUTH_OFFSET;
    const my = Position.y[eid] + Math.sin(heading) * MOUTH_OFFSET;
    spawnFx(ctx, SpriteId.MuzzleFire, mx, my, heading, 2.4, 0xffffff, 0.96);
    emitParticles(ctx.fx, ParticleKind.Embers, mx, my, Math.cos(heading), Math.sin(heading), 3);
  }

  /** Keep the jet's root on the mouth as the dragon drifts and turns. */
  private placeJet(eid: number, jet: number): void {
    const heading = Sprite.rot[eid];
    const hx = Math.cos(heading);
    const hy = Math.sin(heading);
    // The jet frame is centered, so push it out half its length past the mouth.
    const along = MOUTH_OFFSET + JET_HALF_LENGTH;
    Position.x[jet] = Position.x[eid] + hx * along;
    Position.y[jet] = Position.y[eid] + hy * along + DragonTag.bob[eid];
    Sprite.rot[jet] = heading;
    // Flare up over the first tenth of the breath, then hold with a little roar flicker.
    const pt = DragonTag.phaseTime[eid];
    const tIn = Math.min(1, pt / 0.08);
    Sprite.scale[jet] = JET_SCALE * (0.6 + 0.4 * tIn) * (1 + Math.sin(pt * 47) * 0.04);
    Sprite.alpha[jet] = Math.min(1, tIn * 1.5);
  }

  private dropJet(eid: number): void {
    const jet = DragonTag.jet[eid];
    if (jet === -1) return;
    DragonTag.jet[eid] = -1;
    if (!FxTag.flag[jet] || Sprite.id[jet] !== SpriteId.FlameJet) return;
    // Let it burn out quickly rather than vanish.
    Lifetime.t[jet] = Math.min(Lifetime.t[jet], 0.1);
  }

  /** Flame projectiles are metered out across the breath so the jet reads as a stream. */
  private streamFlames(eid: number, hx: number, hy: number, dt: number): void {
    const ctx = this.ctx;
    const c = Config.dragon;
    const jet = DragonTag.jet[eid];
    if (jet !== -1) {
      if (FxTag.flag[jet] && Sprite.id[jet] === SpriteId.FlameJet) this.placeJet(eid, jet);
      else DragonTag.jet[eid] = -1;
    }
    const total: number = c.breathCount;
    const want = Math.min(total, Math.floor((DragonTag.phaseTime[eid] / c.breathDuration) * total) + 1);
    const center = Math.atan2(hy, hx);
    const cone = c.breathConeDeg * DEG;
    const mouthX = Position.x[eid] + hx * MOUTH_OFFSET;
    const mouthY = Position.y[eid] + hy * MOUTH_OFFSET + DragonTag.bob[eid];
    while (DragonTag.breathed[eid] < want) {
      const i = DragonTag.breathed[eid]++;
      // Alternate sides of the cone so consecutive flames fan out instead of clumping.
      const side = (i & 1) === 0 ? 1 : -1;
      const a = center + side * cone * 0.5 * ctx.rng.next();
      const speed = c.flameSpeed * difficulty().projectileSpeed * ctx.rng.range(0.85, 1.15);
      const s = this.flame;
      s.x = mouthX;
      s.y = mouthY;
      s.vx = Math.cos(a) * speed;
      s.vy = Math.sin(a) * speed;
      s.life = c.flameLife * ctx.rng.range(0.8, 1.2);
      s.scale = 1.1 + ctx.rng.next() * 0.5;
      s.weapon = WeaponId.HydraMortar; // any Bolt-pattern id: keeps the FX path non-crescent
      spawnProjectile(ctx, s);
    }
    if (ctx.rng.chance(dt * 30)) emitParticles(ctx.fx, ParticleKind.Embers, mouthX, mouthY, hx, hy, 1.2, 0xffc060);
  }
}